// All tunables for Utopia, grouped by spec section (Section 2 hard rule: every tunable number
// lives here, never inline in logic). Values are the spec's starting constants (Section 23)
// unless marked INVENTED, which means the spec names the effect without a number; those are
// listed in docs/decisions.md. Lines marked PRIMARY BALANCE LEVER are the Section 26 list.
//
// Content ids are derived from the keys of `jobs`, `buildings`, `traits`, `illnesses`, `norms`
// and `events` below (core/types/content-ids.ts). Adding a noun starts here (ADR-0010).
//
// Streams: add constants only under the section comment that cites the spec; never reorder.
import type {
  DifficultyPreset,
  NewGameOptions,
  Policies,
  PriorityGroupId,
  RationPolicyId,
  Resource,
  Season,
} from './core/types';

type OptionsPreset = Omit<NewGameOptions, 'seed'>;

// ---- Section 3: new-game defaults and the Section 26 difficulty presets.
const defaultOptions: OptionsPreset = {
  population: 24,
  land: 'temperate',
  disasterFrequency: 'normal',
  threatLevel: 'low',
  startingStock: 'standard',
  startingKnowledge: 'mixed',
  steadyTargetYears: 10,
  turnCadence: 'season',
  ironman: false,
  observer: false,
  preset: 'standard',
};

// M7 (Section 26 presets): measured with the steward (`npm run balance -- 200 120 --preset=`),
// Gentle reaches the steady state in about 95% of games, Standard about 20 to 25%, Unforgiving
// about 5 to 10%. Every hard option alone costs Standard most of its wins (Harsh land 8%,
// Volatile disasters 6%, Lean stock with Novice knowledge 4%; High threat alone little), and any
// two together leave the steward 0 to 3%, so Unforgiving is Harsh land and High threat on the
// Standard settlement; the 20-year target was out of reach within 30 years (M7-28).
const presets: Record<DifficultyPreset, OptionsPreset> = {
  gentle: {
    ...defaultOptions,
    land: 'fertile',
    disasterFrequency: 'calm',
    threatLevel: 'none',
    startingStock: 'generous',
    startingKnowledge: 'skilled',
    steadyTargetYears: 5,
    preset: 'gentle',
  },
  standard: defaultOptions,
  unforgiving: {
    ...defaultOptions,
    land: 'harsh',
    threatLevel: 'high',
    preset: 'unforgiving',
  },
};

// ---- Section 18: policy defaults ("all defaults are the middle option").
const defaultPolicies: Policies = {
  foodRation: 'floor-priority',
  foodFloor: 0.5,
  foodPriority: [
    'pregnant',
    'children-under-6',
    'farmers',
    'food-producers',
    'water-carriers',
    'everyone-else',
    'idle-adults',
  ],
  seedReserve: 'keep-unless-starvation',
  waterRation: 'equal',
  warmth: 'children-first',
  healthCare: 'most-likely-to-die',
  toolAllocation: 'highest-output',
  housing: { mode: 'families-first', allowOvercrowding: true },
  announceRations: true,
  householdSharing: true,
  childLabour: 'emergencies',
  schooling: 'voluntary',
  schoolSubjects: ['farmer', 'literacy'],
  retirementAge: 60,
  workIntensity: 'normal',
  pairing: 'free',
  migration: 'ask',
  justice: { response: 'restitution', askEachCase: true },
  guardMandate: 'patrol-stores',
  festival: 'off',
  burial: 'field',
  askOnShortage: true,
};

/** Section 17: how the water and warmth options map onto the ration engine. */
interface RationPlan {
  policy: RationPolicyId;
  order: PriorityGroupId[];
}

const workerGroups: PriorityGroupId[] = [
  'farmers',
  'food-producers',
  'water-carriers',
  'builders',
  'toolmakers',
  'healers',
  'teachers',
  'guards',
  'other-workers',
];

const bySeason = (
  spring: number,
  summer: number,
  autumn: number,
  winter: number,
): Record<Season, number> => ({
  spring,
  summer,
  autumn,
  winter,
});

const perResource = (values: Record<Resource, number>): Record<Resource, number> => values;

export const config = {
  meta: {
    /** GameState.version written by newGame. */
    stateVersion: 1,
    /** Save envelope format (24.12). */
    formatVersion: 1,
    /** Numbers on entities are rounded to this on write (24.7). */
    precision: 1e-6,
  },

  // ---- Section 3: new-game options.
  newGame: {
    populationMin: 8,
    populationMax: 200,
    populationPresets: [12, 24, 48, 96],
    defaults: defaultOptions,
    presets,
    /** Age pyramid: ~20% children (0-14), 60% adults (15-59), 20% elders (60+). */
    childShare: 0.2,
    elderShare: 0.2,
    childAgeYears: [0, 14],
    adultAgeYears: [15, 59],
    elderAgeYears: [60, 75], // INVENTED upper bound
    minAdultsPerSex: 2,
    minFarmingSkill: 2,
    householdSizeMin: 2,
    householdSizeMax: 5,
    coupleMaxAgeGapYears: 12, // INVENTED
    parentAgeAtBirthYears: [16, 42],
    /** Probability of 0, 1 or 2 traits (Section 5: at most two). INVENTED weights. */
    traitCountWeights: [0.45, 0.4, 0.15],
    /** Traits that contradict each other are never generated together. */
    exclusiveTraitPairs: [
      ['hardy', 'frail'],
      ['gentle', 'volatile'],
    ] as const,
    /** Section 3: at least one adult with this skill at minFarmingSkill or higher. */
    farmingSkill: 'farmer' as const,
    /** Share of eligible opposite-sex pairs that start as couples. INVENTED. */
    coupleChance: 0.75,
    /** INVENTED starting stats. */
    stats: {
      healthMean: 90,
      healthSd: 6,
      healthMin: 60,
      strengthChild: 60,
      strengthAdult: 80,
      strengthElder: 60,
      strengthSd: 8,
      happinessMean: 60,
      happinessSd: 8,
      trustMean: 60,
      trustSd: 6,
    },
    /**
     * Section 3 starting knowledge presets. INVENTED distributions (D-07): every adult has a
     * base level in the common labour skills, plus specialties drawn from all skills.
     */
    knowledge: {
      novice: { base: 1, skillsMin: 1, skillsMax: 1, levelMin: 0.5, levelMax: 1.5 },
      mixed: { base: 2, skillsMin: 1, skillsMax: 2, levelMin: 1, levelMax: 3 },
      skilled: { base: 3, skillsMin: 2, skillsMax: 3, levelMin: 2, levelMax: 4 },
      elderBonus: 1,
      commonSkills: [
        'farmer',
        'forager',
        'hunter',
        'water-carrier',
        'woodcutter',
        'builder',
      ] as const,
    },
    /** Section 3: seasons of food and firewood on hand (1 / 2 / 4). */
    stockSeasons: { lean: 1, standard: 2, generous: 4 },
    /**
     * P-02: a start whose food, water or firewood exceeds its store by less than this many
     * seasons of need is trimmed to the store (Section 7: over capacity spoils at 3x; the sliver
     * only makes the first screen read as over capacity). Standard at 24 people: two seasons of
     * food is 42 in a store of 40, so 40; its seed shares food's store (ADR-0010 item 10) but is
     * sown in the first Spring before any spoilage. A larger surplus is kept, knowingly over
     * capacity: Generous's four seasons (Section 3), and settlements larger than the base stores
     * were sized for; they spoil at 3x until a granary or woodshed stands, and the first screen
     * warns. INVENTED.
     */
    trimOverCapacitySeasons: 0.25,
    /** INVENTED: the rest of the starting stock. */
    waterSeasons: 1,
    woodPerPersonSeason: 1,
    /** M7: 1 (was 0.5): a founding crew arrives with a tool each (Section 10's multipliers are
     * most of what makes a skilled settlement productive; the toolmaker then keeps them up). */
    toolsPerAdult: 1,
    startingMedicine: 3,
    startingCloth: 5,
    startingStone: 10,
    startingFibre: 0,
    /** Section 9: 2 shelters and 1 field for the default 24 people; scaled for other sizes. */
    peoplePerStartingShelter: 12,
    peoplePerStartingField: 24,
    /**
     * The founding crew (api/assign.ts). 'full' (P-01): nobody who can work starts at rest while
     * a useful post is open. 'm1' (D-23, D-28): the M1 default below, kept for the M2a rationing
     * fixture, whose documented shares depend on that crew (M7-14).
     */
    crewPlan: 'full' as 'full' | 'm1',
    /** The 'm1' crew: a rough water yield per carrier used to size it. M7: 2.4 -> 7.5 with the
     * retuned carrier (M7-08). */
    waterPerCarrierEstimate: 7.5, // INVENTED
    peoplePerWoodcutter: 12, // INVENTED
    startingForagers: 1,
    /** P-01: the 'full' founding crew (Section 25 M1, Sections 8, 9, 15, 18). INVENTED sizing. */
    crew: {
      /** Water is covered for the whole first year with this margin over the need: a carrier
       * of three or four falls ill most years in crowded shelters, the sick drink 1.5x, and
       * dehydration kills fastest (Section 6). */
      waterMargin: 1.5,
      /** Firewood for the year's warmth with this margin (the toolmaker's wood, repairs). */
      woodMargin: 1.2,
      /** A healer and a toolmaker are assigned when someone holds the skill at this level. */
      specialistMinSkill: 2,
      /** Caretakers once this many children are under the caretaker age (Section 8). */
      caretakerMinChildren: 3,
      /** Builders: one per this many people, at least 1, at most maxBuilders (Section 9: the
       * first building the player queues progresses from the first season; two at 24 people). */
      peoplePerBuilder: 12,
      maxBuilders: 4,
      /** Foragers before hunters (Section 8: the nth forager yields 0.9^(n-1)). */
      maxForagers: 4,
      /** Hunting kills (Section 8: 2% a season): at most this share of the people hunt. */
      maxHunterShare: 0.1,
      /** Past the hunters, more foragers while the next gathers this share of their own yield. */
      minForagerYieldShare: 0.4,
      /** Posts a retiree takes when they hold the skill (Section 15: "elders are the natural
       * teachers and caretakers"; Section 18: past the retirement age they may volunteer). */
      volunteerJobs: ['healer', 'teacher', 'caretaker'] as string[],
    },
  },

  // ---- Section 4: seasons.
  seasons: {
    order: ['spring', 'summer', 'autumn', 'winter'] as Season[],
    /**
     * Crop multiplier on the Autumn plot yield. S4 Summer "small early harvest 0.3x" of the
     * spec's 4.0; M7 keeps about the spec's absolute 1.2 a plot against the 14.5 main harvest
     * (ADR-0020, M7-02).
     */
    crops: bySeason(0, 0.08, 1.0, 0),
    forage: bySeason(0.8, 1.2, 1.0, 0.2),
    /** Water production multiplier. */
    water: bySeason(1.2, 0.7, 1.0, 1.0),
    /** S4 warmth column; demand uses needs.warmthPerBed (S6/S23), see docs/decisions.md D-03. */
    warmthNeed: bySeason(0.5, 0, 0.7, 1.5),
    illness: bySeason(1.0, 0.8, 1.0, 1.6),
  },

  // ---- Section 3 and 23: land, disaster and threat multipliers.
  land: {
    crops: { fertile: 1.25, temperate: 1.0, harsh: 0.75 },
    forage: { fertile: 1.15, temperate: 1.0, harsh: 0.8 }, // INVENTED
    winterSeverity: { fertile: 0.9, temperate: 1.0, harsh: 1.25 }, // INVENTED, Winter warmth demand
    waterTable: { fertile: 1.1, temperate: 1.0, harsh: 0.85 }, // INVENTED, water carrier output
    wellFreezeChance: { fertile: 0, temperate: 0, harsh: 0.25 }, // INVENTED, per Winter (S9)
  },
  disasterFrequency: { calm: 0.5, normal: 1.0, volatile: 1.75 },
  threatLevel: { none: 0, low: 1.0, high: 2.0 }, // high INVENTED

  // ---- Section 5: people.
  people: {
    seasonsPerYear: 4,
    adultAtSeasons: 60,
    elderAtSeasons: 240,
    statMax: 100,
    memorySeasons: 8,
    maxTraits: 2,
    traitInheritChance: 0.3,
  },
  traits: {
    hardy: { healthLossMul: 0.7 },
    frail: { healthLossMul: 1.4, illnessMul: 1.3 },
    diligent: { outputMul: 1.15 },
    'quick-learner': { skillGainMul: 1.5 },
    devout: { unequalRationTrustMul: 0.5, foodHappinessWeight: 0.5 },
    restless: { emigrationPressureMul: 1.25, idleHappinessMul: 1.5 }, // INVENTED magnitudes ("threshold lower")
    fertile: { conceptionMul: 1.5 },
    gentle: { violentCrimeMul: 0 },
    volatile: { lowTrustCrimeMul: 2, outputMul: 1.1 },
    charismatic: { spreadHouseholds: 2, spreadMul: 0.3 },
  },

  // ---- Section 6: needs.
  needs: {
    foodAdult: 1.0,
    foodChild: 0.6,
    foodElder: 0.8,
    foodPregnant: 1.3,
    heavyJobFoodSurcharge: 0.2,
    waterPerPerson: 1.0,
    sickWaterMul: 1.5,
    bedsPerPerson: 1,
    /** Warmth per household bed by season (S6, S23: 0.5 Autumn, 1.0 Winter). */
    warmthPerBed: bySeason(0, 0, 0.5, 1.0),
    clothWarmth: 0.3,
    medicinePerPatient: 1,
    /** INVENTED: below this fraction of need a person counts as "short" for streaks. PRIMARY BALANCE LEVER (S26 adjacent). */
    shortThreshold: 0.9,
    /** Fraction of need that counts as "met" for the steady-state check (1.0 of need). */
    metThreshold: 0.999,
    shelterShortHappiness: -8,
    shelterWinterHealth1: -8,
    warmthShortHappiness: -5,
    warmthShortHealth1: -5,
    foodShortHappiness: -10,
    safetyShortHappiness: -6,
    safetyShortTrust: -4,
    belongingShortHappiness: -4,
    belongingShortHappiness2: -8,
    belongingCrimeMul: 1.3,
    safetyChildLearningMul: 0.5,
    untreatedSeriousDeath: 0.15,
    stuntedAfterSeasons: 4,
    stuntedStrengthCap: 80,
    stuntedSkillGainMul: 0.8,
    elderShortHealthLossMul: 1.3,
    pregnantShortMiscarriage: 0.25,
    newbornHealthIfShort: 60,
  },

  // ---- Section 7: resources, stockpiles and spoilage.
  resources: {
    baseCapacity: perResource({
      food: 40,
      water: 30,
      wood: 60,
      stone: 60,
      tools: 20,
      medicine: 15,
      cloth: 30,
      fibre: 30,
    }),
    /** Per season, uncovered. Food 3% in a granary (25% damaged); water 0% in a cistern. */
    spoilage: perResource({
      food: 0.1,
      water: 0.2,
      wood: 0.02,
      stone: 0,
      tools: 0,
      medicine: 0.15,
      cloth: 0.01,
      fibre: 0.01,
    }),
    /** Theft exposure: high 1, medium 0.5, none 0. */
    theftExposure: perResource({
      food: 1,
      water: 0,
      wood: 0.5,
      stone: 0,
      tools: 1,
      medicine: 0.5,
      cloth: 0.5,
      fibre: 0.5,
    }),
    overCapacitySpoilageMul: 3,
    /** INVENTED: stock above capacity x this is lost at once (also the invariant bound). */
    hardCapFactor: 25,
    historySeasons: 12,
  },

  // ---- Section 8: jobs. `yield` is per fully productive worker per season.
  jobs: {
    farmer: {
      /**
       * Autumn food per plot; Summer is seasons.crops.summer x this, about 1.2. PRIMARY BALANCE
       * LEVER (S26). M7: 4.0 -> 14.5, seed 0.5 -> 1.25, two plots a farmer (ADR-0020, M7-02).
       */
      plotYield: 14.5,
      seedPerPlot: 1.25,
      /** M7: the Autumn seed reserve covers this many seasons of food spoilage before Spring. */
      seedSpoilageSeasons: 2,
      plotsPerFarmer: 2,
      toolFactor: 1.5,
      injury: 0.02,
    },
    forager: {
      yield: 1.4,
      fibre: 0.3,
      diminishing: 0.9,
      toolFactor: 1.2,
      injury: 0.03,
      animal: 0.01,
    },
    hunter: {
      yield: 1.8,
      cloth: 0.2,
      toolWear: 0.1,
      toolFactor: 1.6,
      winterFactor: 0.6,
      injury: 0.06,
      death: 0.02,
      deathHighThreat: 0.04,
    },
    /** M7: 3.0 -> 10.0, well +2.0 -> +6.0 (M7-01). */
    'water-carrier': { yield: 10.0, wellBonus: 6.0, toolFactor: 1.3, illness: 0.01 },
    /** M7: 3.0 -> 4.0 (M7-05). */
    woodcutter: { yield: 4.0, toolFactor: 1.8, injury: 0.05 },
    quarrier: { yield: 2.0, toolFactor: 1.8, injury: 0.06 },
    builder: { yield: 3, toolFactor: 1.4, injury: 0.04 },
    toolmaker: { yield: 1.5, woodPerTool: 1, stonePerTool: 0.5, workshopFactor: 1.5, injury: 0.01 },
    healer: {
      treats: 3,
      treatsWithClinic: 6,
      gatherMedicine: 0.5,
      literateGatherMul: 1.5,
      illness: 0.03,
      plagueCatch: 0.1,
    },
    teacher: { students: 6, minSkill: 2 },
    caretaker: {
      covers: 5,
      uncoveredHappiness: -10,
      uncoveredIllnessMul: 1.5,
      parentOutputLoss: 0.2,
    },
    guard: {
      safetyPoints: 1,
      toolWear: 0.05,
      toolFactor: 1.3,
      minStrength: 40,
      injury: 0.03,
      raidInjury: 0.1,
      raidDeath: 0.03,
    },
    weaver: { yield: 1.0, fibrePerCloth: 1.5, loomFactor: 1.5 },
    rest: { strengthBonus: 15, idleSeasonsBeforePenalty: 4 },
  },
  /** Section 8 and 23: the productivity formula. */
  work: {
    childWorkFromYears: 12,
    childYieldMul: 0.5,
    /** f(S): piecewise linear through (20, 0.2), (50, 0.55), (80, 1.0). PRIMARY BALANCE LEVER (S26). */
    strengthCurve: [
      [20, 0.2],
      [50, 0.55],
      [80, 1.0],
    ] as [number, number][],
    skillBase: 0.6,
    skillPerLevel: 0.1,
    happinessBase: 0.9,
    happinessSpan: 0.2,
    pregnancyThirdSeasonOutput: 0.7,
    trustBonusAbove: 75,
    trustBonusOutput: 1.05,
  },

  // ---- Section 9: buildings.
  buildings: {
    shelter: { buildPoints: 6, wood: 8, stone: 0, beds: 4, warmthFactor: 1.0 },
    house: { buildPoints: 14, wood: 6, stone: 10, beds: 5, warmthFactor: 0.7, fireRisk: 0.3 },
    field: { buildPoints: 4, wood: 2, stone: 0, plots: 4 },
    granary: {
      buildPoints: 10,
      wood: 10,
      stone: 4,
      foodCapacity: 60,
      foodSpoilage: 0.03,
      damagedSpoilage: 0.25,
    },
    well: { buildPoints: 8, wood: 2, stone: 8, fouledWaterIllness: 0.5, peoplePerWell: 30 },
    cistern: { buildPoints: 8, wood: 4, stone: 6, waterCapacity: 40, waterSpoilage: 0 },
    woodshed: { buildPoints: 5, wood: 6, stone: 0, woodCapacity: 60, warmthEfficiency: 1.2 },
    workshop: { buildPoints: 12, wood: 10, stone: 6, toolCapacity: 20, limit: 1 },
    clinic: {
      buildPoints: 12,
      wood: 8,
      stone: 8,
      medicineCapacity: 15,
      contagion: 0.7,
      peoplePerClinic: 50,
    },
    school: { buildPoints: 10, wood: 10, stone: 2, seats: 12 },
    'watch-post': { buildPoints: 6, wood: 6, stone: 0, safetyPoints: 1, guardFactor: 1.5 },
    palisade: {
      buildPoints: 20,
      wood: 24,
      stone: 0,
      raidDamage: 0.5,
      wildAnimals: 0.3,
      guardsRequired: 2,
      limit: 1,
    },
    'common-hall': { buildPoints: 14, wood: 12, stone: 6, trustRecovery: 1.5, limit: 1 },
    cemetery: { buildPoints: 4, wood: 0, stone: 4, griefMul: 0.5, limit: 1 },
    jail: { buildPoints: 8, wood: 4, stone: 8, cells: 4, limit: 1 },
  },
  building: {
    decayPerSeason: 1,
    upkeepFraction: 0.1,
    upkeepEverySeasons: 8,
    cancelRefund: 0.5,
    fireConditionStone: 60,
    fireConditionWood: 20,
    /** INVENTED: effects are full at or above this condition and scale down linearly below it. */
    fullEffectCondition: 50,
    overcrowdedHappiness: -5,
    crowdingIllnessPerExtra: 1.3,
    crowdingIllnessCap: 2, // INVENTED cap on the compounding crowding factor
    /**
     * S16 trust table "a promised build finished (player queued and completed a shelter or
     * well)": completing a queued building of these types marks `building-complete.promised`.
     */
    promisedBuildTypes: ['shelter', 'well'] as string[],
  },

  // ---- Section 10: tools.
  tools: {
    wearPerUse: 0.1,
    startDurability: 1.0,
    /** S22 right-panel example "No toolmaker for 5 seasons": warn before the decay starts. */
    noToolmakerWarnSeasons: 5,
    /** S10: from this many seasons without a toolmaker, idle tools wear as if in use (B-03). */
    noToolmakerDecaySeasons: 8,
  },

  // ---- Section 11: education and skills.
  skills: {
    max: 5,
    gainDoing: 0.15,
    gainSchool: 0.25,
    gainApprentice: 0.35,
    masterMinSkill: 4,
    masterOutputMul: 0.85,
    maxApprentices: 2,
    literateAfterSchoolSeasons: 8,
    literateGainBonus: 0.05,
    decayPerSeason: 0.05,
    decayAfterIdleSeasons: 2,
    decayFloor: 1.0,
    decayFloorOnceReached: 3.0,
    teacherMinSkill: 2,
    knowledgeLostMinSkill: 4,
    knowledgeHolderMinSkill: 3,
    schoolAgeFromYears: 6,
    schoolAgeToYears: 14,
    /** Work term of the happiness target: "skilled at current job" means at least this level. INVENTED. */
    skilledAt: 3,
    /** S18 schooling: compulsory options keep children up to this age (years, inclusive) out of work. */
    compulsoryToYears: {
      none: null,
      voluntary: null,
      'compulsory-12': 12,
      'compulsory-14': 14,
    } as Record<Policies['schooling'], number | null>,
    /**
     * S11 "literate adults ... eligible to be teacher in any subject at skill 2": a literate teacher
     * may teach any subject up to this level (their own skill if higher). D-30.
     */
    literateTeachLevel: 2,
    /** S6 safety row: children's learning rate halves after this many seasons short of safety. */
    safetyShortLearningSeasons: 2,
  },

  // ---- Section 12: health, disease, healers and medicine.
  illness: {
    /** PRIMARY BALANCE LEVER (S26). */
    baseChance: 0.03,
    hungryMul: 1.5,
    dehydratedMul: 2,
    coldMul: 1.5,
    noShelterMul: 1.5,
    wellMul: 0.5,
    sickCarrierMul: 1.5,
    contagionPerSickShare: 0.4,
    clinicContagionMul: 0.7,
    underSixMul: 1.5,
    noMedicineDeathMul: 0.5,
    healerVisitHealth: 8,
    lowHealthBelow: 40,
    lowHealthStrengthCap: 60,
    wastingAfterHungrySeasons: 4,
    /** S12 "treated": healer and medicine halve the death chance (plague uses its own 10%). C-03. */
    treatedDeathMul: 0.5,
    /** INVENTED (C-04): health-care need satisfaction when treated without medicine (S6 heat map). */
    healthCareWithoutMedicine: 0.5,
  },
  illnesses: {
    fever: {
      weight: 0.5,
      untreated: 2,
      treated: 1,
      death: 0.05,
      deathVulnerable: 0.12,
      contagion: 1,
      healthLoss: 5,
    },
    // Flux's water demand 1.5x (S12) is the S7 sick-water rule (needs.sickWaterMul), applied once.
    flux: { weight: 0.25, untreated: 2, treated: 1, death: 0.08, contagion: 1, healthLoss: 6 },
    injury: {
      weight: 0,
      untreated: 2,
      treated: 1,
      death: 0.02,
      strengthPenalty: 30,
      contagion: 0,
      healthLoss: 3,
    },
    wasting: { weight: 0, untreated: 3, treated: 2, death: 0.15, contagion: 0, healthLoss: 8 },
    plague: {
      weight: 0,
      untreated: 3,
      treated: 2,
      death: 0.25,
      deathTreated: 0.1,
      contagion: 2,
      healthLoss: 10,
    },
  },

  // ---- Section 13: crime, justice and external threats.
  crime: {
    baseChance: 0.01,
    hungerMul: 2,
    lowTrustBelow: 40,
    lowTrustMul: 2,
    highTrustAbove: 80,
    highTrustMul: 0.5,
    idleSeasons: 4,
    idleMul: 1.5,
    deterrencePerSafetyPoint: 0.15,
    theftFood: { weight: 0.45, min: 2, max: 6 },
    theftTools: { weight: 0.2, min: 1, max: 2 },
    assault: { weight: 0.2, victimHouseholdTrust: -15, detection: 0.8 },
    sabotage: { weight: 0.1, condition: -20, detection: 0.5 },
    killing: { weight: 0.05, trustBelow: 25, happiness: -8, trust: -6, detection: 0.85 },
    detectionBase: 0.6,
    detectionPerGuard: 0.1,
    detectionCap: 0.95,
    /** S16: settlement trust below config.trust.crimeAndEmigrationBelow (40) doubles crime. */
    lowSettlementTrustMul: 2,
  },
  justice: {
    deterrenceSeasons: 4,
    pardon: { offenderTrust: 5, victimTrust: -10, othersTrust: -3, deterrence: 0.9 },
    restitution: {
      outputMul: 0.8,
      seasons: 4,
      offenderHappiness: -5,
      victimTrust: 5,
      othersTrust: 2,
      deterrence: 1.1,
    },
    imprisonment: { minSeasons: 2, maxSeasons: 4, victimTrust: 8, othersTrust: 3, deterrence: 1.3 },
    flogging: {
      strength: -30,
      health: -10,
      offenderTrust: -25,
      victimTrust: 6,
      othersTrust: -4,
      othersHappiness: -3,
      deterrence: 1.5,
    },
    exile: { victimTrust: 10, othersTrust: -6, deterrence: 1.6, partnerFollows: 0.5 },
    execution: {
      victimTrust: 8,
      othersTrust: -10,
      othersHappiness: -6,
      devoutTrust: -20,
      deterrence: 2.0,
    },
    watchedHarshTrustMin: -3,
    watchedHarshTrustMax: -10,
  },
  threats: {
    raidMaxLoss: 0.3,
    freeSafetyPoints: 2,
    lossReductionPerExtraPoint: 0.1,
    peoplePerSafetyPoint: 15,
    raidLossSafetyThreshold: 0.1,
    safetyLookbackSeasons: 4,
    /** INVENTED (E1-03): a palisade manned by 2+ guards adds this many safety points (S6, S9). */
    palisadeSafetyPoints: 1,
    /** INVENTED (E1-10): chance a raid fought with no guards takes a person (S13 "may"). */
    kidnapChance: 0.5,
  },

  // ---- Section 14: migration.
  migration: {
    groupSizeMin: 1,
    groupSizeMax: 8,
    claimTruth: 0.8,
    sickVisible: 0.7,
    guestRation: 0.5,
    guestSeasons: 2,
    turnAwayDevoutTrust: -2,
    turnAwayDevoutHappiness: -3,
    turnAwayStarvingHappiness: -2,
    sickArrivalContagion: 1.5,
    sickArrivalSeasons: 2,
    rareSkillMin: 3,
    newcomerMarkSeasons: 8,
    newcomerTrust: 40,
    newcomerSeasons: 8,
    /** INVENTED: "many in a short window" = this share of population admitted within the window. */
    manyAdmittedShare: 0.25,
    manyAdmittedWindowSeasons: 4,
    /** PRIMARY-adjacent: emigration pressure threshold and seasons required (S23). */
    emigrationThreshold: 60,
    emigrationSeasons: 3,
    foodPerLeaver: 1,
    departureTrust: -2,
    populationCap: 200,
    /**
     * INVENTED weights of emigrationPressure = f(happiness, trust, safety, hunger, Restless).
     * M7 adds a term for S16 "below 40 ... emigration starts": this much per point a person's
     * own trust is below 40. Before, trust 35 with happiness 70 gave 0.4 x 65 + 0.4 x 30 = 38,
     * far from 60, so a distrusting but fed settlement never lost anyone; now the least
     * trusting households leave within a year below 40 (M7-10).
     */
    pressureHappinessWeight: 0.4,
    pressureTrustWeight: 0.4,
    pressureLowTrustBelow: 40,
    pressureLowTrustWeight: 2,
    /** M7-10: per point the settlement's trust is below 40, for everyone; 0 (3 emptied whole
     * settlements once trust dipped). */
    pressureLowSettlementWeight: 0,
    pressureSafetyWeight: 10,
    pressureHungerPerSeason: 5,
    // Stream E2 (Section 14 arrivals and departures). INVENTED values are listed in decisions.md.
    /** Section 16 "above 75 ... arrivals increase": arrival events weigh this much more. INVENTED. */
    arrivalsTrustMul: 1.5,
    /** INVENTED: chance a generated arrival is sick (fleeing famine: the higher value). */
    arrivalSickChance: 0.1,
    famineSickChance: 0.2,
    /** INVENTED: health lost on the road by people fleeing famine or raiders. */
    refugeeHealthLoss: 15,
    /** INVENTED: a false skill claim inflates a real skill by this much (capped at skills.max). */
    claimInflation: 1.5,
    /** INVENTED: the number of skills an arrival claims (their best ones). */
    claimedSkillCount: 2,
    /** INVENTED: share of a refugee group (after the first couple) who are children. */
    refugeeChildShare: 0.5,
    /** INVENTED: share of refugee groups fleeing famine (the rest flee raiders). */
    famineShare: 0.5,
    /** Section 14: departures leave at the start of the next Spring or Summer. */
    departureSeasons: ['spring', 'summer'] as Season[],
    /** Section 14 and 13: an exiled person's children under this age go with them. */
    dependantUnderSeasons: 60,
    /** INVENTED: a decided arrival group's record is kept this many seasons (UI, Ledger). */
    groupRecordSeasons: 8,
  },

  // ---- Section 15: life cycle.
  lifecycle: {
    pairingMinAgeYears: 16,
    pairingMaxAgeYears: 45,
    pairingChance: 0.08,
    pairingNoBedsFertilityMul: 0.5,
    conceptionMinAgeYears: 16,
    conceptionMaxAgeYears: 42,
    /** S23 12%; M7: 10% (M7-29, the Section 20 population band). */
    conceptionBase: 0.1,
    /** Happiness factor: 0.3 at 30, 1.0 at 70, linear between and clamped outside. */
    conceptionHappinessCurve: [
      [30, 0.3],
      [70, 1.0],
    ] as [number, number][],
    conceptionHungryFactor: 0,
    conceptionSurplusFactor: 1.2,
    conceptionOvercrowdedFactor: 0.5,
    conceptionFamilySizeFactor: 0.6,
    conceptionFamilySizeAt: 3,
    pregnancySeasons: 3,
    maternalDeath: 0.04,
    maternalDeathHealer: 0.02,
    maternalDeathHealerCloth: 0.01,
    infantDeath: 0.08,
    infantDeathHealer: 0.04,
    clothPerBirth: 1,
    newbornHealth: 100,
    newbornStrength: 50, // INVENTED
    /** S5 "sex affects only pregnancy eligibility": a newborn is a girl with this chance. */
    newbornFemaleChance: 0.5,
    caretakerUnderYears: 6,
    childIllnessMul: 1.5,
    strengthCapFullUntilYears: 45,
    strengthCapDropPerYear: 2,
    strengthCapFloor: 50,
    elderHealthDecay: 1.5,
    elderDeathAt60: 0.01,
    elderDeathAt85: 0.12,
    elderDeathMax: 0.5, // INVENTED cap when extrapolating past 85
    griefSeasons: 2,
    griefHappiness: -15,
    griefOutputMul: 0.85,
    deathHappinessNoCemetery: -3,
    deathHappinessCemetery: -1,
    pairingMinAgeByPolicy: { free: 16, 'min-18': 18, 'min-21': 21, 'player-approves': 16 },
    /** D-32: pairs form between a woman and a man (the S15 conception model is sex-based, S26). */
    pairingOppositeSexOnly: true,
    /** INVENTED (D-32): pairing weight by the pair's mean happiness ("weighted by happiness"). */
    pairingHappinessCurve: [
      [0, 0.25],
      [50, 1.0],
      [100, 1.5],
    ] as [number, number][],
    /** INVENTED (D-32): pairing weight when both work the same job ("and shared work"). */
    pairingSharedWorkMul: 1.5,
    /**
     * INVENTED (D-33): a household "had surplus" (conception food factor 1.2) when the couple was fed
     * in full and the food left after the season covers this many seasons of settlement demand.
     */
    conceptionSurplusSeasons: 1,
  },

  // ---- Section 16: happiness, trust, norms, inequality, festivals.
  happiness: {
    base: 50,
    needsSpan: 30,
    belongingSpan: 10,
    familySpan: 10,
    skilledWork: 5,
    idle: -5,
    safetySpan: 8,
    festival: 10,
    grief: -15,
    approachRate: 0.4,
    /** INVENTED (D-34): family term share for someone with no partner, parent or child in the settlement. */
    familyAloneShare: 0.25,
  },
  trust: {
    /** PRIMARY BALANCE LEVER (S26): trust penalty for unequal rations. */
    rationedShort: -8,
    rationedShortAnnouncedMul: 0.5,
    rationedShortEqual: -2,
    childRationedShort: -12,
    fedFullWhileOthersShort: 1,
    treatedShortMedicine: 6,
    passedOverTreatment: -8,
    givenTool: 3,
    allNeedsMet: 2,
    promisedBuild: 3,
    festival: 4,
    deprivationDeathAll: -5,
    deprivationDeathHousehold: -15,
    idlePerSeason: -1, // INVENTED (Section 8 rest row names the effect)
    crimeAndEmigrationBelow: 40,
    refusalBelow: 25,
    breakdownBelow: 15,
    breakdownSeasons: 3,
    bonusAbove: 75,
    /** INVENTED (D-35): chance per season of a refusal while settlement trust is below refusalBelow. */
    refusalChance: 0.5,
    /** INVENTED (D-35): share of households (lowest mean trust first, at least one) that refuse. */
    refusalHouseholdShare: 0.25,
    /**
     * S9 common hall and S16 merciful justice "trust recovery": the positive trust causes these
     * multiply. Streams adding positive causes list them here.
     */
    recoveryCauses: [
      'fed-full-while-others-short',
      'treated-short-medicine',
      'given-tool',
      'all-needs-met',
      'promised-build',
      'festival',
    ] as string[],
  },
  normRules: {
    establishAfter: 4,
    decayAfter: 8,
  },
  norms: {
    'workers-first': { nonWorkerTrustMul: 0.5, childHappiness: -3 },
    'children-first': { adultShortTrust: -1 },
    'elders-last': { elderHappiness: -5, elderEmigrationMul: 0.5, elderTeachMul: 0.8 },
    'equal-shares': { unequalOverrideTrustMul: 2 },
    'founders-first': {
      newcomerTrust: 25,
      arrivalsMul: 0.5,
      /** Events whose draw weight "arrivals 0.5x" scales (Section 19 arrival events). */
      arrivalEvents: ['refugees', 'travellers', 'stranger'] as string[],
    },
    'harsh-justice': { deterrence: 1.3, gentleDevoutTrustPerSeason: -1 },
    'merciful-justice': { crimeMul: 1.2, trustRecoveryMul: 1.3 },
  },
  festival: {
    foodPerPerson: 1,
    wood: 2,
    happiness: 10,
    trust: 4,
    seasons: 2,
  },

  // ---- Section 17: rationing.
  rationing: {
    floors: [0.25, 0.5, 0.75],
    householdSharingFraction: 0.25,
    announcedDeviationMul: 2,
    waterPlans: {
      equal: { policy: 'equal', order: [] },
      'sick-first': { policy: 'priority', order: ['sick-or-injured', 'everyone-else'] },
      'workers-first': { policy: 'priority', order: [...workerGroups, 'everyone-else'] },
      manual: { policy: 'manual', order: ['everyone-else'] },
    } as Record<Policies['waterRation'], RationPlan>,
    warmthPlans: {
      equal: { policy: 'equal', order: [] },
      'children-first': {
        policy: 'priority',
        order: ['children-under-6', 'children-6-14', 'everyone-else'],
      },
      'elders-first': { policy: 'priority', order: ['elders', 'everyone-else'] },
      'workers-first': { policy: 'priority', order: [...workerGroups, 'everyone-else'] },
    } as Record<Policies['warmth'], RationPlan>,
    /** S17 priority groups: "Children under 6" is below this age in years; 6 to 14 is the rest of childhood. */
    youngChildUnderYears: 6,
    /** S17 "Warmth ... with a Winter-only override list": warmth pins apply only in these seasons (A-05). */
    warmthPinSeasons: ['winter'] as Season[],
    /** S7 "winter warmth substitute (1 cloth = 0.3 warmth)": seasons cloth may stand in for firewood. */
    clothWarmthSeasons: ['winter'] as Season[],
  },

  // ---- Section 18: policies.
  policies: {
    defaults: defaultPolicies,
    announceOffTrust: -3,
    childLabourLoosenedTrust: -4,
    retirementRaisedTrust: -3,
    workIntensityHardTrust: -3,
    hardOutput: 1.15,
    hardStrength: -8,
    hardInjury: 1.5,
    lightOutput: 0.85,
    lightStrength: 5,
    escortForagerRisk: 0.5,
    escortTheftDeterrence: 0.8,
    /** INVENTED (E1-06): "Deter only" guards stand watch instead of patrolling the stores. */
    deterOnlyViolentDeterrence: 1.2,
    executionUnlockTrust: 60,
  },

  // ---- Section 19: events. baseChance per season before season bias and option multipliers.
  eventDeck: {
    maxPerSeason: 2,
    /** INVENTED: weight multiplier outside an event's biased seasons. */
    offSeasonMul: 0.25,
    foreshadowShare: 0.5,
    // Stream E2 (Section 19 deck, 24.13). The deck is drawn from the event stream two seasons
    // ahead so a warning can precede it; `weight(ctx)` only thins what was drawn (ADR-0016).
    /** Events whose weight is multiplied by the Threat level option (Section 3). */
    threatEvents: ['raid', 'wild-animals'] as string[],
    /**
     * The most `weight(ctx)` can exceed the base chance by (thinning needs a cap): arrivals rise
     * 1.5x above trust 75 (Section 16); birth complications are 10% per birth, capped at 3 births.
     */
    weightCaps: {
      refugees: 1.5,
      travellers: 1.5,
      stranger: 1.5,
      'birth-complication': 3,
    } as Partial<Record<string, number>>,
    /** INVENTED: a false warning is issued at this share of the real warning rate. */
    falseWarningShare: 0.5,
  },
  events: {
    'poor-harvest': { baseChance: 0.1, harvestMul: 0.6, foreshadowChance: 0.6 },
    blight: {
      baseChance: 0.05,
      harvestMul: 0.3,
      seedMul: 0.5,
      burnLoss: 0.3,
      persistChance: 0.5,
      /** The blighted year's harvests: Summer and Autumn (Section 8). */
      seasons: 2,
    },
    drought: {
      baseChance: 0.07,
      waterMul: 0.5,
      forageMul: 0.7,
      cropsMul: 0.7,
      seasons: 2,
      /** INVENTED: "dig deeper (builder points now)": this season's builders dig instead of build. */
      digBuilderOutputMul: 0,
    },
    'harsh-winter': { baseChance: 0.12, warmthMul: 1.5, illnessMul: 1.3, forageMul: 0 },
    'early-frost': { baseChance: 0.06, harvestMul: 0.8 },
    fire: {
      baseChance: 0.05,
      condition: -60,
      storedFoodLoss: 0.3,
      injuries: 1,
      /** INVENTED: "palisade reduces": a manned palisade halves the chance of a fire. */
      palisadeMul: 0.5,
    },
    'fever-outbreak': {
      baseChance: 0.06,
      minSick: 3,
      maxSick: 6,
      quarantineFoodPerSick: 1,
      /** INVENTED: a quarantine is held for the untreated fever duration (Section 12). */
      quarantineSeasons: 2,
    },
    plague: {
      baseChance: 0.015,
      seeded: 2,
      contagionMul: 2,
      closeGatesSeasons: 4,
      /** INVENTED: a quarantine is held for the untreated plague duration (Section 12). */
      quarantineSeasons: 3,
    },
    'wild-animals': {
      baseChance: 0.06,
      childTaken: 0.2,
      huntHunters: 2,
      huntInjury: 0.15,
      huntFood: 4,
      avoidForageMul: 0.7,
      avoidSeasons: 2,
    },
    raid: {
      baseChance: 0.05,
      payFoodShare: 0.2,
      hideLossShare: 0.4,
    },
    refugees: { baseChance: 0.08, minSize: 2, maxSize: 8 },
    travellers: {
      baseChance: 0.06,
      minSize: 1,
      maxSize: 3,
      /** INVENTED: "skilled adults": each has one skill in this range. */
      skillMin: 3,
      skillMax: 4.5,
    },
    'trade-caravan': {
      baseChance: 0.08,
      offers: 2,
      /** INVENTED barter table (Section 14: "10 wood for 6 food"); a caravan offers two of them. */
      deals: [
        { give: 'wood', giveAmount: 10, get: 'food', getAmount: 6 },
        { give: 'food', giveAmount: 6, get: 'tools', getAmount: 2 },
        { give: 'stone', giveAmount: 8, get: 'food', getAmount: 5 },
        { give: 'cloth', giveAmount: 4, get: 'medicine', getAmount: 3 },
        { give: 'food', giveAmount: 5, get: 'wood', getAmount: 10 },
        { give: 'wood', giveAmount: 8, get: 'tools', getAmount: 2 },
        { give: 'fibre', giveAmount: 6, get: 'cloth', getAmount: 3 },
        { give: 'tools', giveAmount: 1, get: 'stone', getAmount: 8 },
      ] as { give: Resource; giveAmount: number; get: Resource; getAmount: number }[],
    },
    'good-harvest': { baseChance: 0.08, harvestMul: 1.3 },
    'mild-winter': { baseChance: 0.08, warmthMul: 0.6, illnessMul: 0.8 },
    discovery: {
      baseChance: 0.05,
      bonusSeasons: 4,
      foragersSent: 2,
      /** Herb patch: medicine per season; fish run: food per working forager per season. */
      medicinePerSeason: 1,
      foodPerForager: 1,
      /** Clay bed with a spring: a new well site (the well limit rises by this). */
      wellSites: 1,
    },
    'improvement-proposal': {
      baseChance: 0.04,
      yieldBonus: 0.1,
      kilnCostMul: 0.8,
      liningSpoilage: 0.01,
      workerMinSkill: 3,
    },
    dispute: { baseChance: 0.06, loserTrust: -8, mediateMinTrust: 60, mediateTrust: -2 },
    'elders-request': {
      baseChance: 0.04,
      /** INVENTED: the elder's mood when the request is granted or refused. */
      grantHappiness: 5,
      refuseHappiness: -5,
      refuseTrust: -5,
      /** Section 11: an elder with skill 4+ asks to teach. */
      teachMinSkill: 4,
    },
    'birth-complication': { baseChance: 0.1 },
    stranger: {
      baseChance: 0.03,
      healerChance: 0.6,
      /** INVENTED: the stranger healer's skill range. */
      healerSkillMin: 3,
      healerSkillMax: 4.5,
    },
    rumour: {
      baseChance: 0.03,
      trustBelow: 45,
      minHouseholds: 1,
      maxHouseholds: 3,
      /** INVENTED: addressing it is a gathering: food per person in the wavering households. */
      addressFoodPerPerson: 1,
      addressTrust: 4,
    },
  },

  // ---- Section 20: steady state, loss, fragility.
  steady: {
    lookbackSeasons: 4,
    /** PRIMARY BALANCE LEVER (S26). */
    needsCoverage: 0.95,
    populationBand: 0.15,
    populationBandSeasons: 12,
    minPopulation: 8,
    birthsLookbackYears: 3,
    minBirths: 1,
    minUnder15Share: 0.12,
    stockTrendSeasons: 12,
    /**
     * M7 (ADR-0018): the 12-season trend is fitted with a level per season of the year, so the
     * repeating harvest peak and Winter draw-down are not read as a decline; false = the plain
     * least-squares line over the 12 values.
     */
    stockTrendBySeason: true,
    /**
     * M7 (ADR-0018): the trend is a share of the larger of the stock's mean and this share of its
     * capacity (the spec's "of the mean" alone fails small stocks on ordinary swings).
     */
    stockTrendCapacityShare: 0.5,
    /**
     * S20 "-2% per year of the mean". M7: -25%. Steward games that pass every other condition
     * swing by +/-15% a year from harvests, building and toolmaking (p5 about -25%), so -2% failed
     * a steady settlement one year in two; -25% still fails a store that halves in two years.
     */
    stockSlopeMinPerYear: -0.25,
    stockTrendResources: ['food', 'water', 'wood', 'tools'] as Resource[],
    minBuildingCondition: 50,
    minTrust: 50,
    knowledgeMinSkill: 3,
    knowledgeSkills: ['farmer', 'water-carrier', 'builder', 'healer'] as const,
  },
  loss: {
    minAdults: 6,
    minPopulation: 8,
    noFertileNoArrivalSeasons: 8,
    breakdownTrust: 15,
    breakdownSeasons: 3,
  },
  /** INVENTED weights (S20 names the terms): each term is normalised to 0..1, weights sum to 100. */
  fragility: {
    foodWeight: 30,
    weaknessWeight: 20,
    illnessWeight: 15,
    safetyWeight: 10,
    trustWeight: 15,
    knowledgeWeight: 10,
    foodSeasonsSafe: 4,
    weakStrengthBelow: 50,
    illnessSpan: 2,
    trustSafe: 60,
  },

  // ---- Section 21: Ledger comparison tags.
  ledger: {
    egalitarianGini: 0.08,
    openGateShare: 0.8,
    closedGateShare: 0.8,
    foundersClubGap: 0.1,
    /**
     * M7 (G-04): the Founders' club compares the rate of early (not old-age) deaths per season
     * lived in the settlement, once this many newcomers have lived this many seasons here and
     * this many more newcomers died early than the founders' rate would give (observed ages
     * favoured the older founders: an open-gate run read "Newcomers lived shorter lives" on their
     * youth alone; with few deaths, one of chance decided the verdict).
     */
    foundersClubMinNewcomers: 3,
    foundersClubMinSeasons: 8,
    foundersClubMinExcessDeaths: 2,
    learnedLiteracy: 0.6,
  },

  // ---- Section 23: strength and health update formulas.
  bodies: {
    strengthFoodGain: 15,
    strengthWaterGain: 5,
    /** PRIMARY BALANCE LEVER (S26): strength loss per hungry season. */
    strengthFoodLoss: 25,
    strengthWaterLoss: 15,
    strengthCold: 10,
    strengthInjured: 30,
    strengthRest: 15,
    strengthFloor: 20,
    healthAllNeedsMet: 4,
    healthHungry2: 12,
    healthDehydrated2: 15,
    healthExposedWinter: 15,
    healthCold2: 10,
  },

  // ---- Section 25 (M5d), 24.8 and 24.10: the AI steward, the default-policy player used by
  // observer mode and the balance harness. These are the steward's heuristics, not game rules;
  // all INVENTED (docs/decisions.md G-10). M7 may tune them alongside the game constants.
  steward: {
    /** Seasons projected per candidate plan (24.8: project(state, candidate, 4)). */
    horizon: 4,
    /** Candidate plans compared by projected fragility per decision, at most. */
    maxCandidates: 3,
    /** Person-seasons projected per decision, over all candidates; large settlements compare
     * fewer candidates over a shorter horizon (Section 2: 200 people under 200 ms). */
    projectionBudget: 800,
    /** Shortest horizon worth projecting. */
    minProjectedSeasons: 2,
    /** Water: plan this share of next season's demand, less this share of the stock on hand. */
    waterMargin: 1.15,
    waterStockCredit: 0.5,
    /** Food: the default plan covers this many seasons ahead; the cautious one the horizon. */
    foodPlanSeasons: 2,
    /** Food and wood: seasons of demand to keep on hand through the horizon. */
    foodBufferSeasons: 0.5,
    woodBufferSeasons: 0.25,
    /** Prefer another forager while its marginal yield is at least this share of a hunter's;
     * hunting kills (Section 8: 2% a season), so at most this share of people hunt. */
    foragerOverHunter: 0.5,
    maxHunterShare: 0.08,
    /** Leftover workers forage while the next forager still yields this share of the first. */
    leftoverMinYieldShare: 0.4,
    /** At most this share of workers farms (the rest carry water, cut wood and build); plots
     * are sized for the year's food times this margin (spoilage, the Winter and Spring gap). */
    maxFarmShare: 0.6,
    farmMargin: 1.3,
    /** Settlements at least this size keep a healer, a toolmaker, a guard. */
    healerMinPopulation: 10,
    toolmakerMinPopulation: 16,
    guardMinPopulation: 20,
    /** M7: guards from this year on, and this many when there are outside threats (a raid is
     * fought with 2+ guards, Section 13; the palisade needs 2). */
    guardFromYear: 2,
    raidGuards: 2,
    /** M7: safety points kept above the Section 6 need by this factor (guards vary in output). */
    safetyMargin: 1.15,
    /** M7: a forager or hunter is added for food only while they would gather at least this
     * much this season (a Winter forager gathers a fifth of a Summer one). */
    minMarginalFood: 0.6,
    /** Builders: one per this many people, at least 1, at most maxBuilders. */
    peoplePerBuilder: 12,
    maxBuilders: 4,
    /** Queue at most this many unfinished orders; repair buildings below this condition. */
    maxQueuedOrders: 2,
    repairBelowCondition: 60,
    /** M7: at most this many granaries (a second one when the first is nearly full). */
    maxGranaries: 2,
    /** M7: keep one builder at work while the best builder's skill is below this (Section 20
     * knowledge wants 3+; skill decays when unused). */
    keepSkillBelow: 3.5,
    /** Make tools while tools on hand are below this many per tool-using worker. */
    toolsPerWorker: 1,
    /** M7: stone kept on hand for tools and upkeep (a quarrier works below it). */
    stoneReserve: 6,
    /** The "recover" candidate rests workers below this strength. */
    restBelowStrength: 35,
    /** Compare candidates by projection when this share of workers is weak (below 50). */
    weakShareTrigger: 0.2,
    /** Candidate score per projected season: fragility, plus expected deaths, the share of
     * people short of water, food and warmth, and mean health lost, each times its weight. */
    scoreDeathWeight: 30,
    scoreWaterShortWeight: 30,
    scoreFoodShortWeight: 15,
    scoreWarmthShortWeight: 10,
    scoreHealthWeight: 0.5,
    /** M7: a candidate other than the balanced plan is chosen only when it scores at least this
     * much lower (per projected season). */
    switchMargin: 4,
    /** Shortage answers eat seed only when someone would get at most this share of need. */
    starvationShare: 0.25,
    /** M7: ... and only for someone already hungry whose health is at most this. */
    starvationHealth: 25,
    /** Arrivals under "ask each time": admit when food covers this many seasons for everyone. */
    admitFoodSeasons: 2,
    /** M7: ... and while the population stays within this share above its level 12 seasons ago
     * (the Section 20 band is 15%; births need room too). */
    admitGrowthShare: 0.08,
    /** M7: a clinic, a common hall and a cemetery from this population; stone houses, a
     * cemetery, a watch post and (High threat) a palisade from this year. */
    hallMinPopulation: 20,
    laterBuildsFromYear: 3,
    /** Children 6 to 14 before a school is built; spare beds kept before building shelter. */
    schoolMinChildren: 4,
    spareBeds: 2,
    /** M7: a current holder ranks as if this much more skilled (crews are not reshuffled). */
    holderSkillBonus: 1,
    /** M7: jobs whose skilled holders the steward keeps out of common work when it can. */
    specialistJobs: ['healer', 'builder', 'teacher', 'toolmaker'] as string[],
    /** M7: jobs in which the steward apprentices a worker to a master (Section 11). */
    apprenticeJobs: ['healer', 'builder', 'water-carrier'] as string[],
    /** M7: posts a resting retiree may be asked to volunteer for (Section 15, 18). */
    volunteerJobs: ['healer', 'teacher', 'caretaker'] as string[],
    /** Children under 6 per caretaker, and the fewest worth a caretaker. */
    childrenPerCaretaker: 5,
    minCaretakerChildren: 5,
  },
};

export type Config = typeof config;
